import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from './ui/Dialog';
import { Button } from './ui/Button';
import { FileText, TrendingUp, TrendingDown } from 'lucide-react';
import { useState, useEffect } from 'react';
import { apiClient } from '../lib/api-client';
import { Skeleton } from './ui/Skeleton';

interface JiraDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  tenantCode: string;
  tenantName: string;
  currentMonth: string; // YYYY-MM
}

export function JiraDetailsDialog({ open, onClose, tenantCode, tenantName, currentMonth }: JiraDetailsDialogProps) {
  const [loading, setLoading] = useState(true);
  const [monthlyData, setMonthlyData] = useState<any[]>([]);
  const [currentData, setCurrentData] = useState<any>(null);

  useEffect(() => {
    if (!open) {
      // Reset state when closed
      setLoading(true);
      setMonthlyData([]);
      setCurrentData(null);
      return;
    }
    
    setLoading(true);
    
    // Fetch last 3 months of data (current + 2 previous) to avoid Jira rate limiting
    const fetchData = async () => {
      try {
        const months: any[] = [];
        const currentDate = new Date(currentMonth + '-01');
        
        // Get 3 months (current + 2 previous)
        for (let i = 2; i >= 0; i--) {
          const d = new Date(currentDate);
          d.setMonth(d.getMonth() - i);
          const monthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          
          try {
            const data = await apiClient.getServiceHealthMonthly(tenantCode, monthStr);
            months.push({ month: monthStr, ...data });
          } catch (e) {
            months.push({ month: monthStr, error: true });
          }
        }
        
        setMonthlyData(months);
        setCurrentData(months[months.length - 1]); // Last entry is current month
      } catch (e) {
        console.error('Failed to fetch Jira history:', e);
      } finally {
        setLoading(false);
      }
    };
    
    void fetchData();
  }, [open, tenantCode, currentMonth]);

  // Don't block rendering - show dialog immediately with loading state
  if (!open) return null;

  const jira = currentData?.jira;
  const counts = jira?.counts || {};

  // Prepare monthly trend table (last 3 months)
  const trendData = monthlyData
    .filter(m => !m.error && m.jira?.configured)
    .map((m, idx, arr) => {
      const date = new Date(m.month + '-01');
      const created = m.jira?.counts?.createdThisMonth ?? 0;
      const resolved = m.jira?.counts?.resolvedThisMonth ?? 0;
      
      let createdTrend: 'up' | 'down' | 'same' = 'same';
      let resolvedTrend: 'up' | 'down' | 'same' = 'same';
      
      if (idx > 0) {
        const prev = arr[idx - 1];
        const prevCreated = prev.jira?.counts?.createdThisMonth ?? 0;
        const prevResolved = prev.jira?.counts?.resolvedThisMonth ?? 0;
        
        if (created > prevCreated) createdTrend = 'up';
        else if (created < prevCreated) createdTrend = 'down';
        
        if (resolved > prevResolved) resolvedTrend = 'up';
        else if (resolved < prevResolved) resolvedTrend = 'down';
      }
      
      return {
        month: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        created,
        createdTrend,
        resolved,
        resolvedTrend,
      };
    });

  const TrendIcon = ({ trend }: { trend: 'up' | 'down' | 'same' }) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-red-500" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-green-500" />;
    return <span className="w-4 h-4" />;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Monthly Service Management Report - {tenantName}
            </div>
          </DialogTitle>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Monthly Summary: {new Date(currentMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </div>
        </DialogHeader>
        <DialogBody className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="space-y-6">
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : !jira?.configured ? (
            <div className="py-12 text-center text-gray-500 dark:text-gray-400">
              Ticket integration is not configured for this tenant
            </div>
          ) : (
            <div className="space-y-8">
              {/* Month Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">Backlog (Total)</div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{counts.backlogTotal ?? 0}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    P1 {counts.backlogP1 ?? 0} · P2 {counts.backlogP2 ?? 0} · P3 {counts.backlogP3 ?? 0}
                  </div>
                </div>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">Created (Month)</div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{counts.createdThisMonth ?? 0}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    P1 {counts.p1CreatedThisMonth ?? 0} · P2 {counts.p2CreatedThisMonth ?? 0} · P3 {counts.p3CreatedThisMonth ?? 0}
                  </div>
                </div>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">Resolved (Month)</div>
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400">{counts.resolvedThisMonth ?? 0}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    P1 {counts.p1ResolvedThisMonth ?? 0} · P2 {counts.p2ResolvedThisMonth ?? 0} · P3 {counts.p3ResolvedThisMonth ?? 0}
                  </div>
                </div>
              </div>

              {/* 3 Month Trend */}
              <div>
                <h3 className="text-lg font-semibold mb-3">3 Month Trend</h3>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Month</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Created</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Resolved</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {trendData.map((row, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-3 text-sm">{row.month}</td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center gap-2">
                              <span>{row.created}</span>
                              <TrendIcon trend={row.createdTrend} />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center gap-2">
                              <span>{row.resolved}</span>
                              <TrendIcon trend={row.resolvedTrend} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  (Arrows compare each month to the immediate predecessor.)
                </div>
              </div>

              {/* Current Backlog by Priority */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Current Backlog by Priority</h3>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Priority</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Number of Tickets</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {(counts.backlogP1 ?? 0) > 0 && (
                        <tr>
                          <td className="px-4 py-3 text-sm">P1 - Critical</td>
                          <td className="px-4 py-3 text-sm font-semibold text-red-600">{counts.backlogP1}</td>
                        </tr>
                      )}
                      {(counts.backlogP2 ?? 0) > 0 && (
                        <tr>
                          <td className="px-4 py-3 text-sm">P2 - High</td>
                          <td className="px-4 py-3 text-sm font-semibold text-orange-600">{counts.backlogP2}</td>
                        </tr>
                      )}
                      {(counts.backlogP3 ?? 0) > 0 && (
                        <tr>
                          <td className="px-4 py-3 text-sm">P3 - Medium</td>
                          <td className="px-4 py-3 text-sm font-semibold text-blue-600">{counts.backlogP3}</td>
                        </tr>
                      )}
                      {/* Some tenants track P4 separately in Jira. If your template provides backlogP4, it will render here. */}
                      {(counts as any).backlogP4 > 0 && (
                        <tr>
                          <td className="px-4 py-3 text-sm">P4 - Low</td>
                          <td className="px-4 py-3 text-sm font-semibold text-blue-600">{(counts as any).backlogP4}</td>
                        </tr>
                      )}
                      {(counts.backlogP1 ?? 0) === 0 && (counts.backlogP2 ?? 0) === 0 && (counts.backlogP3 ?? 0) === 0 && (counts.backlogTotal ?? 0) > 0 && (
                        <tr>
                          <td className="px-4 py-3 text-sm">All Priorities</td>
                          <td className="px-4 py-3 text-sm font-semibold">{counts.backlogTotal}</td>
                        </tr>
                      )}
                      {(counts.backlogTotal ?? 0) === 0 && (
                        <tr>
                          <td colSpan={2} className="px-4 py-3 text-sm text-center text-gray-500">No backlog</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Incidents Raised and Resolved by Priority */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Incidents Raised and Resolved by Priority</h3>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Priority</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Raised</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Resolved</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {(counts.p1CreatedThisMonth ?? 0) > 0 && (
                        <tr>
                          <td className="px-4 py-3 text-sm">P1 - Critical</td>
                          <td className="px-4 py-3 text-sm">{counts.p1CreatedThisMonth}</td>
                          <td className="px-4 py-3 text-sm">{counts.p1ResolvedThisMonth ?? 0}</td>
                        </tr>
                      )}
                      {(counts.p2CreatedThisMonth ?? 0) > 0 && (
                        <tr>
                          <td className="px-4 py-3 text-sm">P2 - High</td>
                          <td className="px-4 py-3 text-sm">{counts.p2CreatedThisMonth}</td>
                          <td className="px-4 py-3 text-sm">{counts.p2ResolvedThisMonth ?? 0}</td>
                        </tr>
                      )}
                      {(counts.p3CreatedThisMonth ?? 0) > 0 && (
                        <tr>
                          <td className="px-4 py-3 text-sm">P3 - Medium</td>
                          <td className="px-4 py-3 text-sm">{counts.p3CreatedThisMonth}</td>
                          <td className="px-4 py-3 text-sm">{counts.p3ResolvedThisMonth ?? 0}</td>
                        </tr>
                      )}
                      {(counts as any).p4CreatedThisMonth > 0 && (
                        <tr>
                          <td className="px-4 py-3 text-sm">P4 - Low</td>
                          <td className="px-4 py-3 text-sm">{(counts as any).p4CreatedThisMonth}</td>
                          <td className="px-4 py-3 text-sm">{(counts as any).p4ResolvedThisMonth ?? 0}</td>
                        </tr>
                      )}
                      {(counts.p1CreatedThisMonth ?? 0) === 0 && (counts.p2CreatedThisMonth ?? 0) === 0 && (counts.p3CreatedThisMonth ?? 0) === 0 && (
                        <tr>
                          <td colSpan={3} className="px-4 py-3 text-sm text-center text-gray-500">No priority-specific data available</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* SLA Status by Priority */}
              {((counts.slaBreached ?? 0) > 0 || (counts.slaAchieved ?? 0) > 0) && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">SLA Status by Priority</h3>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Priority</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold">SLA Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        <tr>
                          <td className="px-4 py-3 text-sm">All Priorities</td>
                          <td className="px-4 py-3 text-sm">
                            <span className="text-green-600 font-semibold">Achieved</span>
                            {(counts.slaBreached ?? 0) > 0 && (
                              <span className="text-red-600 font-semibold ml-2">({counts.slaBreached} breached)</span>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter className="flex-shrink-0">
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

